import { useState, useCallback, useEffect } from "react";
import { Modal, ModalBody, ModalHeader, Button, Form } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";

import ImageUpload from "components/Common/ImageUpload";
import {
  useUploadImages,
  useBulkDeleteImages,
  updateVehicleImagesCache,
} from "hooks/imageHook";
import { useToastMessage } from "hooks/useToastMessage";
import ButtonTooltip from "components/Common/ButtonTooltip";
import WarningAlert from "components/Common/WarningAlert";
import { useEditVehicleStock } from "hooks/stockHook";
import { useQueryClient } from "@tanstack/react-query";

export default function StockImagesUpdate({ stock = null }) {
  const [modal, setModal] = useState(false)
  const [existingImages, setExistingImages] = useState([])
  const [imagesToDelete, setImagesToDelete] = useState([])
  const [imagesToUpdate, setImagesToUpdate] = useState([]) // For reordering
  const toggle = () => setModal(!modal)

  const { handleResponse } = useToastMessage()
  const uploadImagesMutation = useUploadImages()
  const bulkDeleteImagesMutation = useBulkDeleteImages()
  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock()

  // Set existing images when stock changes
  useEffect(() => {
    if (stock?.images) {
      setExistingImages(stock.images)
    }
  }, [stock?.images])

  const validationSchema = Yup.object({
    images: Yup.array().of(Yup.mixed()),
  })

  const queryClient = useQueryClient()

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      images: [],
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: useCallback(
      async values => {
        try {
          const formData = new FormData()

          // Handle new uploads
          if (values?.images?.length) {
            values.images.forEach(file => {
              formData.append("images[]", file)
            })
          }

          // Handle deletions
          if (imagesToDelete?.length) {
            imagesToDelete.forEach(id => {
              formData.append("delete_file_ids[]", id)
            })
          }

          // Process changes
          if (formData.has("images[]") || formData.has("delete_file_ids[]") || imagesToUpdate.length > 0) {
            
            // Handle uploads/deletions first if any
            let finalImages = [...existingImages]
            
            if (formData.has("images[]") || formData.has("delete_file_ids[]")) {
              const uploadRes = await uploadImagesMutation.mutateAsync(formData)
              
              if (uploadRes.success && uploadRes.data) {
                // Filter out deleted images
                finalImages = existingImages.filter(
                  img => !imagesToDelete.includes(img.id)
                )
                
                // Add new images
                finalImages = [
                  ...finalImages,
                  ...uploadRes.data.map(img => ({
                    id: img.id,
                    url: img.url,
                    isPrimary: false,
                  })),
                ]
              }
            } else if (imagesToDelete.length > 0) {
              // Only deletions
              finalImages = existingImages.filter(
                img => !imagesToDelete.includes(img.id)
              )
            }

            // Apply reordering if any
            if (imagesToUpdate.length > 0) {
              const reorderedMap = new Map(
                imagesToUpdate.map((img, idx) => [img.id, idx])
              )
              
              finalImages.sort((a, b) => {
                const orderA = reorderedMap.get(a.id) ?? Infinity
                const orderB = reorderedMap.get(b.id) ?? Infinity
                return orderA - orderB
              })
            }

            // Update stock with final images array
            const stockRes = await editStock({
              id: stock?.id,
              images: finalImages,
            })

            // Update cache
            const updatedImageIds = stockRes?.data?.images || []
            const auctionId = stockRes?.data?.auction_sheet?.id || null

            const query = {
              vehicleId: stock?.id,
              images: updatedImageIds,
              auctionId,
              queryClient,
            }

            updateVehicleImagesCache(query)

            handleResponse(stockRes, "Images updated successfully.", true)
            if (stockRes.success) {
              setImagesToDelete([])
              setImagesToUpdate([])
              toggle()
              validation.resetForm()
            }
          } else {
            handleResponse(
              { success: false, message: "No changes made" },
              "Please select images to upload or delete.",
              false
            )
          }
        } catch (error) {
          console.error("Error updating images:", error)
          handleResponse(
            { success: false, message: error.message },
            "Failed to update images. Please try again.",
            false
          )
        }
      },
      [
        uploadImagesMutation,
        bulkDeleteImagesMutation,
        editStock,
        handleResponse,
        stock?.id,
        existingImages,
        imagesToDelete,
        imagesToUpdate,
        queryClient
      ]
    ),
  })

  // Handle image updates from ImageUpload component
  const handleImageUpdate = useCallback(({ 
    allImages, // All images (existing + new)
    deletedIds, // IDs to delete
    reorderedImages, // Reordered existing images
    primaryImageId // New primary image ID
  }) => {
    
    // Update existing images with reordering
    if (reorderedImages) {
      setExistingImages(reorderedImages)
      setImagesToUpdate(reorderedImages)
    }
    
    // Update deletion list
    if (deletedIds) {
      setImagesToDelete(prev => [...new Set([...prev, ...deletedIds])])
    }
    
    // Handle primary image
    if (primaryImageId) {
      const updatedImages = existingImages.map(img => ({
        ...img,
        isPrimary: img.id === primaryImageId
      }))
      setExistingImages(updatedImages)
      
      // Update stock with new primary image
      editStock({
        id: stock?.id,
        images: updatedImages,
      }).then(stockRes => {
        if (stockRes.success) {
          handleResponse(stockRes, "Primary image updated successfully.", true)
        }
      }).catch(error => {
        console.error("Error setting primary image:", error)
        handleResponse(
          { success: false, message: error.message },
          "Failed to set primary image. Please try again.",
          false
        )
      })
    }
    
    // Update formik values with new files
    if (allImages) {
      const newFiles = allImages
        .filter(img => img.file) // Only files, not existing images
        .map(img => img.file)
      validation.setFieldValue("images", newFiles)
    }
  }, [existingImages, editStock, stock?.id, handleResponse, validation])

  // Reset form when modal opens
  useEffect(() => {
    if (modal) {
      validation.setValues({ images: [] })
      setImagesToDelete([])
      setImagesToUpdate([])
    }
  }, [modal])

  const isProcessing =
    uploadImagesMutation.isPending ||
    bulkDeleteImagesMutation.isPending ||
    stockEditLoading

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId="edit-images"
        btnName="Edit Images"
      />

      <Modal isOpen={modal} toggle={toggle} centered size="xl">
        <ModalHeader toggle={toggle}>Manage Images</ModalHeader>
        <ModalBody>
          <WarningAlert message="Please ensure images are clear and properly formatted. Drag to reorder - first image will be primary." />

          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            <ImageUpload
              existingImages={existingImages}
              onImagesChange={handleImageUpdate}
              maxFiles={30}
              maxSizeMB={5}
              acceptedFileTypes="image/*"
              multiple={true}
              title="Vehicle Images"
              subtitle="Drag & drop images here to upload. Drag existing images to reorder. Click star to set as primary."
              disabled={isProcessing}
            />

            <div className="text-end mt-4">
              <Button
                type="submit"
                color="success"
                disabled={
                  isProcessing ||
                  (validation.values.images.length === 0 &&
                    imagesToDelete.length === 0 &&
                    imagesToUpdate.length === 0)
                }
              >
                {isProcessing ? "Processing..." : "Save Changes"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}